import { useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useRecoilState, useSetRecoilState } from 'recoil';

import {
  cookiesHelper,
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  ID_TOKEN,
  GetAuthAPI,
  APP_REFERER,
  authState,
  companyState,
  Actions,
  Subjects,
} from '../..';
import { AppErrorResponse } from '../services/axios';
import { UserInfoDto } from '@jaam/commons/dtos';

const navigate = (path: string): void => {
  window.location.href = path;
};

const getAuthLoginUrl = (appReferrer: APP_REFERER): string => {
  const base = typeof process.env !== 'undefined' && process.env['NX_AUTH_PUBLIC_URL'];
  return base ? `${base}/login?appReferer=${appReferrer}` : '/login';
};

interface UseAuthParams {
  onSuccess?: (data: any) => void;
  onError?: (error: AppErrorResponse) => void;
  appReferrer: APP_REFERER;
}

export const useGlobalAuth = (
  { onSuccess, onError, appReferrer }: UseAuthParams = {
    appReferrer: APP_REFERER.PAYROLLR,
  },
) => {
  const AUTH_API = GetAuthAPI(appReferrer);

  const fetchAuth = async () => {
    const response = await AUTH_API.get<UserInfoDto>('/auth/getMyInfo');
    return response.data;
  };

  const [auth, setAuth] = useRecoilState(authState);
  const setCompanyData = useSetRecoilState(companyState);

  const { data, isPending, isError, error } = useQuery<
    UserInfoDto,
    AppErrorResponse
  >({
    queryKey: ['auth'],
    queryFn: fetchAuth,
    retry: 1,
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      cookiesHelper.remove(ACCESS_TOKEN);
      cookiesHelper.remove(REFRESH_TOKEN);
      cookiesHelper.remove(ID_TOKEN);
      navigate('/login');
    },
  });
  if (data) {
    if (auth?.authStatus === 'unauthenticated') {
      setAuth((prev) => ({
        ...prev,
        authStatus: 'authenticated',
        user: data.user,
        parsedPermissions: data.user.permissions
          ? data.user.permissions?.map((perm) => {
              return {
                action: perm.action as Actions,
                subject: perm.permissionObject?.name as Subjects,
                conditions: perm?.conditions,
              };
            })
          : [],
      }));
      setCompanyData((prev) => ({
        ...prev,
        activeCompany: data.activeCompany,
      }));
      onSuccess?.(data);
    }
  }

  if (isError) {
    if (auth !== null) {
      onError?.(error);
    }
  }

  // Quand getMyInfo échoue (ex: 500), rediriger vers la page auth et nettoyer les tokens (toutes les client-apps)
  useEffect(() => {
    if (!isError) return;
    cookiesHelper.remove(ACCESS_TOKEN);
    cookiesHelper.remove(REFRESH_TOKEN);
    cookiesHelper.remove(ID_TOKEN);
    const url = getAuthLoginUrl(appReferrer);
    window.location.href = url;
  }, [isError, appReferrer]);

  return {
    auth: data || auth,
    activeCompany: data?.activeCompany || null,
    isPending,
    isError,
    error,
    isAuthenticated: !!data,
    logout: () => logoutMutation.mutate(),
    isLoggingOut: logoutMutation.isPending,
  };
};

/**
 * Hook dédié au logout pour les composants qui n'utilisent pas useGlobalAuth.
 * Utilise useMutation de React Query comme useGlobalAuth.
 */
export const useLogout = () => {
  const logoutMutation = useMutation({
    mutationFn: async () => {
      cookiesHelper.remove(ACCESS_TOKEN);
      cookiesHelper.remove(REFRESH_TOKEN);
      cookiesHelper.remove(ID_TOKEN);
      navigate('/login');
    },
  });

  return {
    logout: () => logoutMutation.mutate(),
    isLoggingOut: logoutMutation.isPending,
  };
};
